import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import { AccountSelectionService, AudiencesService, DspStrategyService } from "@front/m19-services";
import { filter, of, switchMap, take, tap } from "rxjs";
import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IBadgeComponent,
  IButtonDirective,
  IDataTableComponent,
  IDataTableSuffix,
  IIconComponent,
  ITableDataSource,
  ITableDirective,
  ITableHeadDirective,
  ITablePagination,
  ITableRowDirective,
  ITableTdDirective,
  ITableTextFilterConfig,
  ITableThDirective,
  ITooltipDirective,
  ModalService,
  SortByConfig,
  SortingOption,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  AsinSelectionModalComponent,
  AsinSelectionModalData,
} from "@m19-board/shared/asin-selection-modal/asin-selection-modal.component";
import { Audience, AudienceStatus } from "@front/m19-api-client";
import { LoadingService } from "@m19-board/services/loading.service";
import { FormsModule } from "@angular/forms";
import {
  DspAudienceInUseModalComponent,
  DspAudienceInUseModalData,
} from "@m19-board/dsp/dsp-audiences-page/dsp-audience-in-use-modal.component";

@Component({
  selector: "app-dsp-audiences-page",
  imports: [
    IButtonDirective,
    ITooltipDirective,
    TranslocoDirective,
    FormsModule,
    ITableThDirective,
    ITableHeadDirective,
    ITableRowDirective,
    ITableTdDirective,
    IBadgeComponent,
    ITableDirective,
    IDataTableComponent,
    IDataTableSuffix,
    RouterLink,
    IIconComponent,
  ],
  templateUrl: "./dsp-audiences-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspAudiencesPageComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly audiencesService = inject(AudiencesService);
  private readonly dspStrategyService = inject(DspStrategyService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly loadingService = inject(LoadingService);
  private readonly toasterService = inject(ToasterService);

  protected readonly AudienceStatus = AudienceStatus;
  private readonly DEFAULT_PAGE_SIZE = 15;

  grid = viewChild.required<AgGridAngular>(AgGridAngular);

  protected readonly sortByOptions: SortingOption<Audience>[] = [
    {
      label: this.translocoService.translate("common.name"),
      sortByField: "audienceName",
    },
    {
      label: this.translocoService.translate("common.status"),
      sortByField: "status",
      compareFn: this.audienceStatusCompareFn,
    },
    {
      label: this.translocoService.translate("common.products"),
      sortByField: "asins",
    },
  ];
  protected readonly sortByConfig = new SortByConfig(this.sortByOptions, this.sortByOptions[1]);

  protected readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { initialValue: true });
  protected deletingAudienceId = signal<string | undefined>("");
  protected paginationConfig = signal<ITablePagination>(new ITablePagination(this.DEFAULT_PAGE_SIZE));
  protected textFilterConfig = new ITableTextFilterConfig<Audience>((audience, filter) =>
    audience.audienceName.toLowerCase().includes(filter.toLowerCase()),
  );

  private audiences$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    filter((am) => !!am.dspAdvertiserId),
    tap(() => this.loadingService.loadingOn()),
    switchMap((am) =>
      am.dspAdvertiserId
        ? this.audiencesService.getDspAdvertiserAudiences(am.accountId, am.marketplace, am.dspAdvertiserId)
        : of([]),
    ),
    tap(() => this.loadingService.loadingOff()),
  );

  protected readonly dataSource = new ITableDataSource(
    this.audiences$,
    this.sortByConfig,
    this.paginationConfig(),
    this.textFilterConfig,
  );

  protected openAsinModal(audience: Audience): void {
    this.modalService.openModal<AsinSelectionModalData, void>(AsinSelectionModalComponent, {
      size: "xl",
      height: "fill",
      modalTitle: this.translocoService.translate("common.products"),
      data: {
        readonly: true,
        asins: audience.asins ?? [],
      },
    });
  }

  openDeleteConfirmationModal(audience: Audience): void {
    if (this.isReadOnly()) return;
    this.dspStrategyService
      .findStrategiesUsingAudience(audience.audienceId)
      .pipe(
        switchMap((strategiesUsingAudience) => {
          if (strategiesUsingAudience.length === 0) return of(true);

          return this.modalService
            .openModal<DspAudienceInUseModalData, boolean>(DspAudienceInUseModalComponent, {
              size: "md",
              modalTitle: this.translocoService.translate("dsp-audiences.audience_in_use_modal_title"),
              data: {
                strategiesUsingAudience,
              },
            })
            .afterClosed();
        }),
        switchMap(() =>
          this.modalService
            .openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
              data: {
                message: this.translocoService.translate("dsp-audiences-page.audience_delete_confirmation", [
                  [audience.audienceName],
                ]),
              },
            })
            .afterClosed(),
        ),
        filter((confirmed) => confirmed),
        tap(() => this.deletingAudienceId.set(audience.audienceId)),
        switchMap(() => this.accountSelectionService.singleAccountMarketplaceSelection$),
        take(1),
        switchMap((am) =>
          this.audiencesService.deleteDspProductAudience(am.accountId, am.marketplace, am.dspAdvertiserId!, audience),
        ),
      )
      .subscribe({
        next: () => {
          this.deletingAudienceId.set(undefined);
          this.toasterService.success(this.translocoService.translate("dsp-audiences-page.audience_delete_success"));
        },
        error: () => {
          this.deletingAudienceId.set(undefined);
          this.toasterService.error(this.translocoService.translate("dsp-audiences-page.audience_delete_error"));
        },
      });
  }

  private audienceStatusCompareFn(a: Audience, b: Audience): number {
    const order = [AudienceStatus.Pending, AudienceStatus.Processing, AudienceStatus.Active, AudienceStatus.Failed];
    return order.indexOf(a.status!) - order.indexOf(b.status!);
  }
}
